import React, { useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useFiscalYear } from '../context/FiscalYearContext';
import useDashboard from '../hooks/useDashboard';
import useEmissionsFormatter from '../hooks/useEmissionsFormatter';
import DashboardResultsTab from '../components/DashboardResultsTab';
import EmptyFiscalYearState from '../components/EmptyFiscalYearState';
import { useLanguage } from '../context/LanguageContext';
import {
  DashboardHeader,
  TrackingTab,
  ObjectivesTab,
  ObjectiveModal
} from '../components/dashboard-page';

const Dashboard = () => {
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const { fiscalYears } = useFiscalYear();
  const hook = useDashboard();
  // ENH-02 LOT A : formatters clos sur summary.total_emissions ; jamais mixte.
  const { formatEmissions, formatChartValue } = useEmissionsFormatter(hook.summary);

  const handleOpenObjectiveModal = useCallback((refId, targetYear) => {
    // FEAT-01h : pré-remplit exclude_extraordinary depuis l'objectif existant
    // (édition) ; false en création (objective null).
    // FIX #1 Temps B : pré-remplit reporting_view depuis l'objectif existant
    // (édition) ; 'market' en création (objective null).
    hook.setObjectiveForm({
      reference_fiscal_year_id: refId,
      target_year: targetYear,
      exclude_extraordinary: hook.objective?.exclude_extraordinary || false,
      reporting_view: hook.objective?.reporting_view || 'market',
    });
    hook.setShowObjectiveModal(true);
  }, [hook]);

  // Loading state
  if (hook.loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!fiscalYears || fiscalYears.length === 0) {
    return <EmptyFiscalYearState contextMessage={t('dashboard.emptyState')} />;
  }

  return (
    <div data-testid="dashboard" className="space-y-6">
      <DashboardHeader
        summary={hook.summary}
        reportingView={hook.reportingView}
        setReportingView={hook.setReportingView}
        excludeExtraordinary={hook.excludeExtraordinary}
        setExcludeExtraordinary={hook.setExcludeExtraordinary}
        hasExtraordinary={hook.hasExtraordinary}
        activeTab={hook.activeTab}
        setActiveTab={hook.setActiveTab}
      />

      {/* Tab 1: Suivi */}
      {hook.activeTab === 'suivi' && (
        <TrackingTab
          stats={hook.stats}
          summary={hook.summary}
          plausibilityResult={hook.plausibilityResult}
          plausibilityLoading={hook.plausibilityLoading}
          runPlausibilityCheck={hook.runPlausibilityCheck}
          dismissPlausibilityAlert={hook.dismissPlausibilityAlert}
          restorePlausibilityAlert={hook.restorePlausibilityAlert}
          formatEmissions={formatEmissions}
          formatChartValue={formatChartValue}
          toggleCategoryCompleted={hook.toggleCategoryCompleted}
          togglingCode={hook.togglingCode}
        />
      )}

      {/* Tab 2: Résultats (already refactored in Phase 1) */}
      {hook.activeTab === 'resultats' && (
        <DashboardResultsTab
          summary={hook.summary}
          kpis={hook.kpis}
          scopeBreakdown={hook.scopeBreakdown}
          fiscalComparison={hook.fiscalComparison}
          formatEmissions={formatEmissions}
          formatChartValue={formatChartValue}
        />
      )}

      {/* Tab 3: Objectifs */}
      {hook.activeTab === 'objectifs' && (
        <div className="space-y-6">
          <ObjectivesTab
            objective={hook.objective}
            trajectoryData={hook.trajectoryData}
            onArchiveObjective={hook.handleArchiveObjective}
            onOpenObjectiveModal={handleOpenObjectiveModal}
            selectedScenarioEntityId={hook.selectedScenarioEntityId}
            setSelectedScenarioEntityId={hook.setSelectedScenarioEntityId}
            scenarioEntities={hook.scenarioEntities}
            scenarioDataPoints={hook.scenarioDataPoints}
            scenarioSummary={hook.scenarioSummary}
            formatEmissions={formatEmissions}
            formatChartValue={formatChartValue}
          />
        </div>
      )}

      {/* Modals */}
      <ObjectiveModal
        show={hook.showObjectiveModal}
        onClose={() => hook.setShowObjectiveModal(false)}
        objectiveForm={hook.objectiveForm}
        setObjectiveForm={hook.setObjectiveForm}
        objectiveLoading={hook.objectiveLoading}
        onSubmit={hook.handleSubmitObjective}
        referenceFyHasExtraordinary={hook.refFyHasExtraordinary}
      />

    </div>
  );
};

export default Dashboard;
